// Public Help / FAQ page. Pulls facility contact info + facility-authored
// custom FAQs from /api/v1/public/faq and renders them with the built-in
// platform Q&As. No auth required.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import LegalLayout from './LegalLayout.jsx';

export default function HelpFaq() {
  const [params] = useSearchParams();
  const code = params.get('fac') || params.get('code') || null;

  const [data, setData] = useState({ contacts: {}, faqs: [], facility: null });
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.publicFaq(code)
      .then(r => { if (alive) setData(r); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [code]);

  const c = data.contacts || {};
  const faqs = data.faqs || [];

  return (
    <LegalLayout title="Help &amp; FAQ" lastUpdated={null}>
      {err && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">Could not load FAQ data: {err}</div>}

      {/* Quick contacts */}
      <section className="not-prose mb-6">
        <h2 className="text-base font-semibold text-slate-800 mb-3">Quick contacts</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Contact label="Pharmacy"     value={c.pharmacy_phone}   tel />
          <Contact label="Hospital"     value={c.hospital_phone}   tel />
          <Contact label="IT support"   value={c.it_support_phone} tel />
          <Contact label="Help desk"    value={c.help_phone}       tel />
          <Contact label="Help email"   value={c.help_email}       email />
        </div>
        {!c.pharmacy_phone && !c.hospital_phone && !c.it_support_phone && !c.help_phone && !c.help_email && (
          <div className="text-sm text-slate-500 italic">
            No contact numbers have been published yet. A facility administrator can add them under <em>Admin → Branding</em>.
          </div>
        )}
      </section>

      {/* Roles cheat-sheet */}
      <h2>Roles &amp; what they can do</h2>
      <ul>
        <li><strong>Nurse</strong> — initiates orders, captures patient consent, hands the device for in-app payment, reviews patient transactions.</li>
        <li><strong>Pharmacy tech</strong> — fulfills prescriptions, marks them dispensed, takes payments at the bedside.</li>
        <li><strong>Pharmacist</strong> — verifies orders, performs counseling, has read access to the audit and payments screens.</li>
        <li><strong>Administrator</strong> — manages users, branding, payments configuration (Stripe Connect onboarding), and reviews the full payments audit.</li>
      </ul>

      {/* FAQs */}
      <h2>Frequently asked questions</h2>
      {faqs.length === 0 && (
        <div className="text-sm text-slate-500">FAQ list is loading…</div>
      )}
      <div className="not-prose space-y-2">
        {faqs.map((f, i) => (
          <details key={i} className="bg-white border border-slate-200 rounded-lg p-4 group open:shadow-sm">
            <summary className="cursor-pointer font-medium text-slate-800 flex items-start gap-2">
              <span className="text-slate-400 group-open:rotate-90 transition">▸</span>
              <span>{f.q}</span>
            </summary>
            <p className="mt-2 ml-6 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{f.a}</p>
          </details>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-8">
        Don't see what you need? Use one of the contact numbers above, or
        <Link to="/login" className="ml-1 underline">sign in</Link> if you're hospital staff.
      </p>
    </LegalLayout>
  );
}

function Contact({ label, value, tel, email }) {
  if (!value) return null;
  const display = tel ? formatPhone(value) : value;
  const href    = tel ? `tel:${value.replace(/[^+\d]/g, '')}`
                : email ? `mailto:${value}`
                : null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      {href
        ? <a href={href} className="text-sm font-medium text-slate-800 hover:underline">{display}</a>
        : <div className="text-sm font-medium text-slate-800">{display}</div>
      }
    </div>
  );
}

// Render raw 10-digit string as "(xxx) xxx-xxxx". Pass through anything else.
function formatPhone(p) {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}
